// SystemHealth.tsx — PWA V2 slice 4 (STEP-PWA-UXV2-DIAGNOSTICS).
//
// Integrates the legacy Diagnostics screen into v4 WITHOUT a 6th tab:
//   • SystemHealthHero — a "System Health" causal hero (Pattern 3) on Home,
//     computed client-side from the SAME data sources the legacy screen uses
//     (/api/pwa/diagnostics + /api/pwa/digital-thread-health).
//   • SystemHealthSheet — the drill-through detail (Pattern 1): a bottom sheet
//     that renders the legacy DiagnosticsScreen wholesale (DR verdict,
//     deduplicated findings by severity, probe coverage, digital-thread
//     health, 7-day trend). Opened via window.__openSystemHealth so the
//     Dispatch tab's "Findings" sub-section routes here instead of
//     duplicating the findings view.

import { useEffect, useState, useCallback } from "react";
import { fetchDiagnostics, fetchDigitalThreadHealth, fetchVlrArrivalRate } from "../api/client.js";
import type {
  DiagnosticsResponse,
  DigitalThreadHealthResponse,
  VlrArrivalRateGaugeResponse,
} from "../api/types.js";
import { HeroZone } from "../components/Heroes.js";
import { TrendSparkline } from "../components/TrendSparkline.js";
import {
  DiagnosticsScreen,
  deduplicateFindings,
  uniqueSeverityCounts,
} from "../screens/Diagnostics.js";

const VERDICT_SEVERITY: Record<string, string> = {
  GREEN: "calm",
  YELLOW: "attention",
  RED: "alert",
};

const SEV_CHIP: Record<string, string> = {
  P0: "danger",
  P1: "danger",
  P2: "warn",
  P3: "",
};

function StatRow({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "7px 0",
        borderBottom: "1px solid var(--border-soft)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
      }}
    >
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span style={{ color: tone, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

const THREAD_POLL_MS = 60_000;

export function SystemHealthHero() {
  const [diag, setDiag] = useState<DiagnosticsResponse | null>(null);
  const [thread, setThread] = useState<DigitalThreadHealthResponse | null>(null);
  const [threadError, setThreadError] = useState(false);
  const [vlrGauge, setVlrGauge] = useState<VlrArrivalRateGaugeResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refreshThread = useCallback(() => {
    fetchDigitalThreadHealth()
      .then((t) => { setThread(t); setThreadError(false); })
      .catch(() => setThreadError(true));
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      fetchDiagnostics().then((d) => {
        if (!cancelled) setDiag(d);
      }),
      fetchDigitalThreadHealth().then((t) => {
        if (!cancelled) { setThread(t); setThreadError(false); }
      }).catch(() => {
        if (!cancelled) setThreadError(true);
      }),
      fetchVlrArrivalRate().then((g) => {
        if (!cancelled) setVlrGauge(g);
      }).catch(() => {
        // non-fatal — gauge degrades gracefully
      }),
    ]).finally(() => {
      if (!cancelled) setLoaded(true);
    });
    const interval = setInterval(() => {
      if (!cancelled) refreshThread();
    }, THREAD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [refreshThread]);

  // Degrade to nothing while loading or when diagnostics are unreachable —
  // Home keeps working without the hero.
  if (!loaded || !diag) return null;

  const verdict = diag.latest_run?.overall_verdict ?? null;
  const deduped = deduplicateFindings(diag.findings ?? []);
  const counts = uniqueSeverityCounts(deduped);
  const runCounts = diag.latest_run?.finding_counts ?? { P0: 0, P1: 0, P2: 0, P3: 0 };
  const coverage = diag.probe_coverage?.coverage_pct ?? 0;
  const severity = verdict ? (VERDICT_SEVERITY[verdict] ?? "watch") : "watch";

  const headline = !verdict
    ? "No diagnostic runs yet"
    : verdict === "RED"
      ? "System health needs intervention"
      : verdict === "YELLOW"
        ? "System health is degraded"
        : "System is healthy";

  const becauseParts: string[] = [];
  becauseParts.push(verdict ? `latest DR run is ${verdict}` : "no completed DR run found");
  if (runCounts.P0 + runCounts.P1 > 0)
    becauseParts.push(`${runCounts.P0 + runCounts.P1} severity-critical in latest run`);
  if (counts.P0 + counts.P1 > 0)
    becauseParts.push(`${counts.P0 + counts.P1} P0/P1 classes open`);
  becauseParts.push(`probes at ${coverage}% coverage`);

  return (
    <>
    <HeroZone
      hero={{
        severity,
        eyebrow: "System Health",
        headline,
        because: becauseParts.join(" · "),
        metric: {
          value: String(deduped.length),
          unit: `open finding${deduped.length === 1 ? "" : "s"}`,
          deltaLabel: diag.latest_run
            ? new Date(diag.latest_run.ran_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "no runs",
        },
      }}
    >
      <div style={{ marginTop: 20 }}>
        {/* Severity signal: latest DR run counts */}
        {diag.latest_run && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)", marginBottom: 4, letterSpacing: "0.04em" }}>
              SEVERITY · LATEST RUN
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(["P0", "P1", "P2", "P3"] as const).map((sev) => (
                <span key={sev} className={`chip ${SEV_CHIP[sev]}`.trim()}>
                  {sev} · {runCounts[sev]}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Priority signal: currently open (deduplicated across all runs) */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)", marginBottom: 4, letterSpacing: "0.04em" }}>
            PRIORITY · OPEN
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(["P0", "P1", "P2", "P3"] as const).map((sev) => (
              <span key={sev} className={`chip ${SEV_CHIP[sev]}`.trim()}>
                {sev} · {counts[sev]}
              </span>
            ))}
          </div>
        </div>
        <StatRow
          label="probe coverage"
          value={`${diag.probe_coverage?.probes_passing ?? 0}/${diag.probe_coverage?.total_probes ?? 0} · ${coverage}%`}
          tone={coverage >= 90 ? "var(--ok)" : coverage >= 70 ? "var(--warn)" : "var(--danger)"}
        />
        <button
          onClick={() => window.__openSystemHealth?.()}
          style={{
            marginTop: 14,
            background: "none",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "8px 14px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--text)",
            cursor: "pointer",
            letterSpacing: "0.04em",
          }}
        >
          Full diagnostics ›
        </button>
      </div>
    </HeroZone>

    {/* Digital Thread / Embedding — separate metrics group */}
    <div className="section-head">
      <h2>Digital Thread & Embedding</h2>
    </div>
    {thread == null ? (
      <div className="card" style={{ padding: "14px 16px" }}>
        <StatRow
          label="digital thread"
          value={threadError ? "UNAVAILABLE" : "Loading..."}
          tone={threadError ? "var(--danger)" : "var(--text-dim)"}
        />
        <StatRow
          label="embedding coverage"
          value={threadError ? "UNAVAILABLE" : "Loading..."}
          tone={threadError ? "var(--danger)" : "var(--text-dim)"}
        />
      </div>
    ) : (
      <div className="card" style={{ padding: "14px 16px" }}>
        <StatRow
          label="annotation coverage"
          value={`${thread.annotationCoverage}% · ${thread.annotatedFiles}/${thread.totalFiles} files`}
          tone={
            thread.annotationCoverage >= 85
              ? "var(--ok)"
              : thread.annotationCoverage >= 70
                ? "var(--warn)"
                : "var(--danger)"
          }
        />
        <div style={{ padding: "4px 0 8px" }}>
          <TrendSparkline
            series={thread.annotationCoverageTrend ?? null}
            height={28}
            color={
              thread.annotationCoverage >= 85
                ? "var(--ok)"
                : thread.annotationCoverage >= 70
                  ? "var(--warn)"
                  : "var(--danger)"
            }
          />
        </div>
        <StatRow
          label="embedding coverage"
          value={`${thread.embeddingCoverage}%`}
          tone={
            thread.embeddingCoverage >= 85
              ? "var(--ok)"
              : thread.embeddingCoverage >= 70
                ? "var(--warn)"
                : "var(--danger)"
          }
        />
        <div style={{ padding: "4px 0 8px" }}>
          <TrendSparkline
            series={thread.embeddingCoverageTrend ?? null}
            height={28}
            color={
              thread.embeddingCoverage >= 85
                ? "var(--ok)"
                : thread.embeddingCoverage >= 70
                  ? "var(--warn)"
                  : "var(--danger)"
            }
          />
        </div>
        <StatRow
          label="orphan modules"
          value={String(thread.orphanCount)}
          tone={
            thread.orphanCount === 0
              ? "var(--ok)"
              : thread.orphanCount <= 9
                ? "var(--warn)"
                : "var(--danger)"
          }
        />
      </div>
    )}

    {/* VLR Arrival Rate section */}
    <VlrArrivalRateSection gauge={vlrGauge} />

    {/* Diagnostics inline section */}
    <DiagnosticsSection diag={diag} />
  </>
  );
}

function DiagnosticsSection({ diag }: { diag: DiagnosticsResponse | null }) {
  if (!diag) return null;

  const deduped = deduplicateFindings(diag.findings ?? []);
  const counts = uniqueSeverityCounts(deduped);
  const openFindings = diag.findings?.filter((f) => f.status === "open") ?? [];

  return (
    <>
      <div className="section-head">
        <h2>Diagnostics</h2>
        <span className="count">{openFindings.length} open</span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "0 20px 8px" }}>
        {(["P0", "P1", "P2", "P3"] as const).map((sev) => (
          <span key={sev} className={`chip ${SEV_CHIP[sev]}`.trim()}>
            {sev} · {counts[sev]}
          </span>
        ))}
      </div>
      {openFindings.length === 0 ? (
        <div className="card" style={{ padding: "14px 16px", textAlign: "center" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ok)" }}>
            No open findings
          </span>
        </div>
      ) : (
        openFindings.slice(0, 10).map((f, i) => (
          <div key={i} className="card" style={{ padding: "10px 14px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--text)",
                  fontWeight: 600,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {f.title}
              </span>
              <span
                className={`chip ${SEV_CHIP[f.severity] ?? ""}`.trim()}
                style={{ fontSize: 9, flexShrink: 0 }}
              >
                {f.severity}
              </span>
            </div>
          </div>
        ))
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// VLR Arrival Rate Section
// ---------------------------------------------------------------------------

function VlrIdList({ ids, label }: { ids: string[]; label: string }) {
  const [expanded, setExpanded] = useState(false);
  if (ids.length === 0) return null;
  const shown = expanded ? ids : ids.slice(0, 3);
  return (
    <div style={{ marginTop: 6 }}>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-muted)",
          marginBottom: 3,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {shown.map((id) => (
          <span
            key={id}
            className="chip"
            style={{ fontSize: 9, fontFamily: "var(--font-mono)" }}
          >
            {id}
          </span>
        ))}
        {ids.length > 3 && (
          <button
            onClick={() => setExpanded((e) => !e)}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "2px 7px",
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              cursor: "pointer",
            }}
          >
            {expanded ? "less" : `+${ids.length - 3} more`}
          </button>
        )}
      </div>
    </div>
  );
}

function VlrArrivalRateSection({ gauge }: { gauge: VlrArrivalRateGaugeResponse | null }) {
  if (!gauge) return null;

  const drainingTone = gauge.is_draining ? "var(--ok)" : "var(--danger)";
  const drainingLabel = gauge.is_draining ? "DRAINING" : "GROWING";
  const { integrated, verified, rejected, cancelled } = gauge.resolutions_by_type;

  return (
    <>
      <div className="section-head">
        <h2>VLR Backlog (7d)</h2>
        <span
          className="chip"
          style={{
            fontSize: 9,
            color: drainingTone,
            borderColor: drainingTone,
            fontFamily: "var(--font-mono)",
            fontWeight: 700,
          }}
        >
          {drainingLabel}
        </span>
      </div>
      <div className="card" style={{ padding: "14px 16px" }}>
        <StatRow
          label="open P0/P1"
          value={String(gauge.open_count)}
          tone={gauge.open_count === 0 ? "var(--ok)" : gauge.open_count <= 5 ? "var(--warn)" : "var(--danger)"}
        />
        <StatRow
          label="arrivals (7d)"
          value={String(gauge.arrivals_7d)}
          tone={gauge.arrivals_7d === 0 ? "var(--ok)" : "var(--warn)"}
        />
        <StatRow
          label="resolutions (7d)"
          value={String(gauge.resolutions_7d)}
          tone={gauge.resolutions_7d >= gauge.arrivals_7d ? "var(--ok)" : "var(--warn)"}
        />
        {gauge.resolutions_7d > 0 && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--text-muted)",
              padding: "4px 0 2px",
              letterSpacing: "0.04em",
            }}
          >
            {[
              integrated > 0 ? `${integrated} integrated` : null,
              verified > 0 ? `${verified} verified` : null,
              rejected > 0 ? `${rejected} rejected` : null,
              cancelled > 0 ? `${cancelled} cancelled` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
        {!gauge.is_draining && gauge.arrivals_7d > 0 && (
          <div
            style={{
              marginTop: 8,
              padding: "6px 10px",
              borderRadius: 6,
              background: "var(--danger-bg, rgba(255,60,60,0.08))",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--danger)",
              fontWeight: 700,
            }}
          >
            Backlog growing — {gauge.arrivals_7d} arrivals, {gauge.resolutions_7d} resolutions (7d)
          </div>
        )}
        <VlrIdList ids={gauge.open_vlr_ids} label="OPEN" />
        <VlrIdList ids={gauge.arrivals_vlr_ids} label="ARRIVED (7D)" />
        <VlrIdList ids={gauge.resolutions_vlr_ids} label="RESOLVED (7D)" />
      </div>
    </>
  );
}

/** Drill-through detail: the legacy Diagnostics screen inside a v4 sheet. */
export function SystemHealthSheet({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="sheet-backdrop open" style={{ position: "fixed" }} onClick={onClose} />
      <div className="sheet open" style={{ position: "fixed" }}>
        <div className="sheet-handle" />
        <div className="sheet-head">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div className="eyebrow">System Health · Diagnostics</div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                color: "var(--text-dim)",
                fontSize: 16,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </div>
        <div className="sheet-body" style={{ padding: 0 }}>
          <DiagnosticsScreen />
        </div>
      </div>
    </>
  );
}
